import {moment} from "moment";
import {
    ICompanySyncstep,
    IImportHrFile,
    IMessageData,
    IRouteDefinitionParams,
    ISyncCycleStep,
    ISyncImportLog,
    ISyncstep
} from "../../index";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {global, lm} from "../../appContext";
import * as ko from "knockout";
import {nsFa} from "../fa/fn";
import {convertFromServerString, getDateTime} from "../../util/datetime";
import {ISortableTableParams} from "../sortable/index";
import {nsRoute} from "../../util/route";
import {createErrorMessage} from "../../util/misc";
import {REQUEST_MESSAGE} from "../../constants/Language";
import {IParams as IEllipsisDropdownParams} from "@component/button/ellipsis-dropdown";
import {nsFile} from "@component/sync/status-v2/nsFile";
import PromiseRequest from "../../req/PromiseRequest";
import {nsSync} from "../../util/nsSync";
import i = nsFa.i;
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import detailsHtml from './details.html?raw';

export let template: string = detailsHtml;

export class viewModel extends ParsedRouteDataDecorator {
    messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);

    isReady: KnockoutObservable<boolean> = ko.observable(false);

    private data!: IImportHrFile;
    private companysyncsteps!: ICompanySyncstep[];
    private syncsteps!: ISyncstep[];

    logSearchKey: KnockoutObservable<string> = ko.observable("");
    private kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly req_getsyncsteps = new PromiseRequest('getsyncsteps');
    private readonly req_getcompanysyncsteps = new PromiseRequest('getcompanysyncsteps');

    constructor(params: IRouteDefinitionParams) {
        super(params);
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE});

        Promise.resolve()
            .then(() => this.__requestRouteData!
                .request()
                .then((data: IImportHrFile[]) => {
                    this.data = data[0]!
                })
            )
            .then(() => this.req_getsyncsteps
                .request()
                .then((syncsteps: ISyncstep[]) => {
                    this.syncsteps = syncsteps
                })
            )
            .then(() => this.req_getcompanysyncsteps
                .request()
                .then((companySyncsteps: ICompanySyncstep[]) => {
                    this.companysyncsteps = companySyncsteps
                })
            )
            .then(() => {
                this.messageData(null);
                this.isReady(true);
            })
            .catch((reason: string) => this.messageData(createErrorMessage(String(reason))))
    }

    filteredLog = ko.pureComputed((): ISyncImportLog[] => {
        const key = this.logSearchKey().toLowerCase();
        return this.data.syncimportlog.filter((item: ISyncImportLog): boolean => {
            const name = item.message.toLowerCase();
            const registered = item.registered.toLowerCase();
            return name.indexOf(key) != -1 || registered.indexOf(key) != -1;
        });
    });

    private setLogFilter(type: string) {
        this.filterLog(type);
        this.__tabManager?.onClick('importhistorylog');
    }

    private readonly tableParams: ISortableTableParams<ISyncImportLog> = {
        TABLE: [{
            key: 'registered',
            sort: true,
        }, {
            key: 'message',
            sort: true,
        }, {
            key: 'type',
            sort: true,
            getContent: (item) => {
                let ret: string;
                if (item.message.indexOf("Finished") === 0) {
                    ret = "fa-check";
                } else if (this.isItemError(item)) {
                    ret = "fa-exclamation-triangle";
                } else {
                    ret = "fa-info-circle";
                }
                return i(ret);
            },
            getCellCss: (item) => {
                if (item.message.indexOf("Finished") === 0) {
                    return "success";
                } else if (this.isItemError(item)) {
                    return "danger";
                } else {
                    return "info";
                }
            },
            center: true,
        }],
        asc: ko.observable(true),
        data: this.filteredLog,
        getCellContent: this.getCellContent.bind(this),
        getCellText: this.getCellText.bind(this),
        getCellSortValue: (key: keyof ISyncImportLog | string, val, item) => {
            switch (key) {
                case "registered":
                    return moment(val).unix();
                case 'type':
                    return (() => {
                        if (item.message.indexOf("Finished") === 0) {
                            return 2;
                        } else if (this.isItemError(item)) {
                            return 0;
                        } else {
                            return 1;
                        }
                    })() + ' ' + this.data.syncimportlog.indexOf(item);
            }
        },
        searchkey: this.logSearchKey,
        sortcol: ko.observable<keyof ISyncImportLog>('registered'),
    }

    private getCellContent(key: keyof ISyncImportLog | string, val: ISyncImportLog[keyof ISyncImportLog], item: ISyncImportLog): string {
        let str = this.getCellText(key, val, item);
        if (this.logSearchKey()) {
            let regex = new RegExp(this.logSearchKey().replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'gi');
            str = String(str).replace(regex, (match) => {
                return "<mark>" + match + "</mark>";
            });
        }
        return str;
    }

    private getCellText(key: keyof ISyncImportLog | string, val: ISyncImportLog[keyof ISyncImportLog], item: ISyncImportLog): string {
        switch (key) {
            case "registered":
                return getDateTime(val, true);
            default:
                return String(val ?? '');
        }
    }

    getDuration(): string | null {
        if (!this.data.started || !this.data.ended) {
            return null;
        }
        const start = convertFromServerString(this.data.started);
        const end = convertFromServerString(this.data.ended);
        const duration: moment.Duration = moment.duration(end.diff(start));
        return moment.duration(duration).humanize()
    }

    private isItemError(item: ISyncImportLog): boolean {
        const regExp = /error occurred|exceeds safety/gi;
        return regExp.test(item.message);
    }

    filterLog(type: string) {
        const l = this.logSearchKey;
        let old = l();
        switch (type) {
            case "errornewaccounts":
                l("An error occurred whilst adding new user");
                break;
            case "erroreditedaccounts":
                l("An error occurred whilst editing user");
                break;
            case "errordeletedaccounts":
                l("An error occurred whilst deleting user");
                break;
            case "errorrecoveredaccounts":
                l("An error occurred whilst recovering user");
                break;
            case "errornewgroups":
                l("An error occurred whilst adding new group");
                break;
            case "erroreditedgroups":
                l("An error occurred whilst editing group");
                break;
            case "errordeletedgroups":
                l("An error occurred whilst deleting group");
                break;
            case "errornewdepartments":
                l("An error occurred whilst adding new department");
                break;
            case "errorediteddepartments":
                l("An error occurred whilst editing department");
                break;
            case "errordeleteddepartments":
                l("An error occurred whilst deleting department");
                break;
        }
        if (l() !== old) {
            global.snackbarData({
                message: 'subject is updated|s:((log))'
            });
        }
    }

    private getSyncstepDropdownParams(synccyclestep: ISyncCycleStep): IEllipsisDropdownParams {
        const btnDownloadFiles = (() => {
            const companySyncstep = this.companysyncsteps.find(o => o.syncstep.name == synccyclestep.syncstepname);
            const syncstep = this.syncsteps.find(o => o.name == synccyclestep.syncstepname);
            const orgnr = global.login.selectedOrganizationId();
            const fileNames = synccyclestep.logfilepaths.split('|');
            const numFiles = fileNames?.length ?? 0;
            const syncstepname = companySyncstep && syncstep
                ? lm.t(nsSync.getSyncstepName(syncstep))
                : synccyclestep.syncstepname
            const params = {
                numFiles: numFiles,
                fileNames: fileNames,
                date: convertFromServerString(synccyclestep.ended).format('YYYY-MM-DD'),
                companySyncstep: companySyncstep,
                importfilesyncstep: synccyclestep,
                syncstepname: syncstepname,
                importfile: this.data,
                companyid: orgnr,
            };
            const btn = {
                cls: 'btn-separated',
                icon: 'fa-download',
                label: {
                    k: 'synccycle.step.btn.downloadfiles',
                    obj: params,
                },
                onClick: () => {
                    nsFile
                        .downloadFile({
                            filename: lm.t({
                                k: `synccycle.step.btn.downloadfiles.filename`,
                                obj: params,
                            }) ?? '',
                            params: {
                                id: synccyclestep.id,
                                orgnr: orgnr,
                                filepath: synccyclestep.logfilepaths,
                            },
                            servicename: 'getfiles',
                        })
                        .catch((reason) => {
                            this.messageData(createErrorMessage(String(reason)))
                        })
                },
                validator: () => !!synccyclestep?.logfilepaths,
            }
            return btn
        })()
        return {
            buttons: [btnDownloadFiles]
        }
    }

    private getSyncstepName(synccyclestep: ISyncCycleStep): string {
        lm.selectedLanguage()
        const syncstep = this.syncsteps.find(o => o.name == synccyclestep.syncstepname);
        return syncstep ? nsSync.getSyncstepName(syncstep) : synccyclestep.syncstepname
    }

    override dispose() {
        this.kosub.unsuscribeAll()
        this.__requestRouteData?.abort()
        this.req_getsyncsteps.abort()
        this.req_getcompanysyncsteps.abort()
    }

}
